import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_HIGH_PROBABILITY_STRIKE_RATE } from '../../graphql/queries/highProbabilitySelections';
import { useBrand } from '../brand';
import { useViewer } from '../shell';
import { useTrack } from '../track';
import { pct } from '../lib/format';
import { PROBABILITY_THRESHOLD, recordPct, type Record as HpRecord } from '../picks/lib';

/**
 * The high-probability page's banner: its name, the 55%+ rule and the last 30 days' record.
 * Members go to today's picks; signed-out visitors to the past days they can see, unless `to` says where.
 */
export function LocksBanner({ to, cta }: { to?: string; cta?: string } = {}) {
  const brand = useBrand();
  const viewer = useViewer();
  const track = useTrack();
  const q = useQuery<{ highProbabilityStrikeRate: HpRecord | null }>(GET_HIGH_PROBABILITY_STRIKE_RATE, {
    variables: { analysisTypeId: viewer.analysisTypeId || 1, minProbability: PROBABILITY_THRESHOLD, timePeriod: '1_MONTH' },
    errorPolicy: 'ignore',
    skip: !brand.features.highProbability,
  });
  if (!brand.features.highProbability) return null;
  const rec = q.data?.highProbabilityStrikeRate;
  const p = recordPct(rec);
  return (
    <Link to={to ?? (viewer.isAuthenticated ? '/high-probability' : '/preview/picks')} className="ui-locks" data-tour="locks" onClick={() => track('ui_click', { target: 'locks_banner' })}>
      <span className="ic" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm0-13a5 5 0 1 0 5 5 5.006 5.006 0 0 0-5-5Zm0 8a3 3 0 1 1 3-3 3 3 0 0 1-3 3Zm0-4a1 1 0 1 0 1 1 1 1 0 0 0-1-1Z" />
        </svg>
      </span>
      <span className="tx">
        <span className="hd">
          <b>{brand.locksName}</b>
          <span className="pill">{PROBABILITY_THRESHOLD}%+ chance</span>
        </span>
        {rec && p ? (
          <span className="st">
            <span className="k">Last 30 days</span>
            <span>
              <b>{pct(p.won)}</b> won
            </span>
            <span>
              <b>{pct(p.top3)}</b> placed
            </span>
            <span className="n">{rec.resulted} runners</span>
          </span>
        ) : (
          <span className="st">Every runner we rate {PROBABILITY_THRESHOLD}% or better to win, across all of today&apos;s meetings.</span>
        )}
      </span>
      <span className="go">
        {cta ?? (viewer.isSubscribed ? "View today's picks" : viewer.isAuthenticated ? "Unlock today's picks" : 'See past picks')} <span aria-hidden="true">→</span>
      </span>
    </Link>
  );
}
